import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Search,
  User,
  ChevronDown,
  Check,
  Loader2,
  Copy,
  X,
  Lock,
  Globe,
  Share2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useKnowledgeBase } from '../../contexts/KnowledgeBaseContext';
import { useUserSearch } from '../../hooks/useUserSearch';
import { useKnowledgeBaseShares } from '../../hooks/useKnowledgeBaseShares';
import { SharePermission } from '../../schemas/knowledgeBase';
import { knowledgeBaseService } from '../../services/knowledgeBaseService';

interface ShareKnowledgeBaseModalProps {
  open: boolean;
  onClose: () => void;
  knowledgeBaseId: string;
  knowledgeBaseName?: string;
  owner?: {
    id: number;
    email: string;
    full_name: string;
  } | null;
  is_public?: boolean;
}

export const ShareKnowledgeBaseModal: React.FC<ShareKnowledgeBaseModalProps> = ({
  open,
  onClose,
  knowledgeBaseId,
  knowledgeBaseName,
  is_public = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { fetchKnowledgeBases, fetchInactiveKnowledgeBases, fetchAllKnowledgeBases, state } =
    useKnowledgeBase();
  const currentUserId = user?.id || 0;

  // Custom hooks
  const {
    query,
    setQuery,
    results: searchResults,
    loading: searchLoading,
  } = useUserSearch(currentUserId);
  const {
    shares,
    loading: sharesLoading,
    addShare,
    updatePermission,
    removeShare,
  } = useKnowledgeBaseShares(knowledgeBaseId);

  // UI State
  // Track local permission state - initialize based on current is_public value
  const [localPermissionType, setLocalPermissionType] = useState<'restricted' | 'anyoneWithLink'>(
    is_public ? 'anyoneWithLink' : 'restricted'
  );
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [openPermissionMenuId, setOpenPermissionMenuId] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [pendingShareId, setPendingShareId] = useState<number | null>(null);

  // Update local permission state when modal opens with new is_public value
  useEffect(() => {
    if (open) {
      setLocalPermissionType(is_public ? 'anyoneWithLink' : 'restricted');
      setHasUnsavedChanges(false);
    }
  }, [open, is_public]);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchDropdownRef.current &&
        !searchDropdownRef.current.contains(event.target as Node) &&
        !searchInputRef.current?.contains(event.target as Node)
      ) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle save button click - apply permission changes
  const handleSave = async () => {
    if (!hasUnsavedChanges) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      const shouldPublic = localPermissionType === 'anyoneWithLink';
      await knowledgeBaseService.updateKnowledgeBase(knowledgeBaseId, {
        is_public: shouldPublic,
      });
      toast.success(
        shouldPublic
          ? t('knowledge.share.success.madePublic')
          : t('knowledge.share.success.madePrivate')
      );

      // Refresh knowledge bases to show updated public/private status
      const currentFilter = state.globalFilter;
      if (currentFilter === 'inactive') {
        await fetchInactiveKnowledgeBases();
      } else if (currentFilter === 'all') {
        await fetchAllKnowledgeBases();
      } else {
        await fetchKnowledgeBases();
      }

      setHasUnsavedChanges(false);
      onClose();
    } catch (error) {
      // Error is handled by global HTTP error handler (toast notification)
    } finally {
      setIsSaving(false);
    }
  };

  // Handle permission change with API call
  const handlePermissionChange = async (
    shareId: number,
    newPermission: SharePermission | 'remove'
  ) => {
    setPendingShareId(shareId);

    try {
      if (newPermission === 'remove') {
        await removeShare(shareId);
        toast.success(t('knowledge.share.success.removed'));
      } else {
        await updatePermission(shareId, newPermission);
        toast.success(t('knowledge.share.success.updated'));
      }
    } catch (error) {
      // Error is handled by global HTTP error handler (toast notification)
    } finally {
      setPendingShareId(null);
      setOpenPermissionMenuId(null);
    }
  };

  // Handle permission menu click
  const handlePermissionMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    shareId: number
  ) => {
    event.stopPropagation();
    if (openPermissionMenuId === shareId) {
      setOpenPermissionMenuId(null);
      setMenuPosition(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setOpenPermissionMenuId(shareId);
      // Position menu below the button, aligned to the left edge of the button
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  };

  // Handle add member with API call
  const handleAddMember = async (userId: number, _userName: string) => {
    // Check if user is already a member
    if (shares.some(share => share.shared_with_user_id === userId)) {
      return;
    }

    setPendingShareId(userId);

    try {
      // Default to 'read' permission when adding
      await addShare({
        shared_with_user_id: userId,
        permission: 'read',
      });
      toast.success(t('knowledge.share.success.added'));
      setQuery('');
      setShowSearchDropdown(false);
    } catch (error) {
      // Error is handled by global HTTP error handler (toast notification)
    } finally {
      setPendingShareId(null);
    }
  };

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setQuery(value);
    setShowSearchDropdown(value.length > 0);
  };

  // Filter out users who are already members
  const availableSearchResults = searchResults.filter(
    user => !shares.some(share => share.shared_with_user_id === user.id)
  );

  // Get current permission for a share
  const getCurrentPermission = (shareId: number): SharePermission => {
    const share = shares.find(s => s.id === shareId);
    return share?.permission || 'read';
  };

  // Get shareable link
  const getShareableLink = () => {
    return `${window.location.origin}/knowledge/${knowledgeBaseId}/files`;
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    const link = getShareableLink();
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      toast.success(t('knowledge.share.linkCopied'));
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      toast.error(t('knowledge.share.copyFailed'));
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center ${open ? 'visible' : 'invisible'}`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0'}`}
          onClick={onClose}
        />

        {/* Modal Content */}
        <div
          className={`relative bg-white rounded-2xl shadow-2xl w-full max-w-[560px] max-h-[90vh] flex flex-col transition-all duration-200 ${open ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
          data-testid='share-modal'
        >
          {/* Header */}
          <div className='flex items-center justify-between px-6 py-4 border-b border-gray-200'>
            <h2 className='text-base font-semibold text-gray-900 flex items-center gap-2'>
              <Share2 size={18} />
              {knowledgeBaseName || t('knowledge.share.memberManagement')}
            </h2>
            <button
              onClick={onClose}
              className='p-1.5 hover:bg-gray-100 rounded-lg transition-colors'
              aria-label={t('common.close')}
            >
              <X size={20} className='text-gray-500' />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className='flex-1 overflow-y-auto px-6 py-5'>
            <div className='space-y-6'>
              {/* General Access Section */}
              <div>
                <h3 className='text-sm font-medium text-gray-900 mb-3'>
                  {t('knowledge.share.generalAccess')}
                </h3>
                <div className='space-y-2'>
                  {/* Restricted Option */}
                  <button
                    onClick={() => {
                      setLocalPermissionType('restricted');
                      setHasUnsavedChanges(true);
                    }}
                    className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-colors text-left ${
                      localPermissionType === 'restricted'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        localPermissionType === 'restricted' ? 'bg-blue-100' : 'bg-gray-200'
                      }`}
                    >
                      <Lock
                        size={20}
                        className={
                          localPermissionType === 'restricted' ? 'text-blue-600' : 'text-gray-600'
                        }
                      />
                    </div>
                    <div className='flex-1'>
                      <div className='flex items-center gap-2'>
                        <p className='text-sm font-medium text-gray-900'>
                          {t('knowledge.share.restricted')}
                        </p>
                        {localPermissionType === 'restricted' && (
                          <Check size={16} className='text-blue-600' />
                        )}
                      </div>
                      <p className='text-xs text-gray-500'>{t('knowledge.share.restrictedDesc')}</p>
                    </div>
                  </button>

                  {/* Anyone with Link Option */}
                  <button
                    onClick={() => {
                      setLocalPermissionType('anyoneWithLink');
                      setHasUnsavedChanges(true);
                    }}
                    className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-colors text-left ${
                      localPermissionType === 'anyoneWithLink'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        localPermissionType === 'anyoneWithLink' ? 'bg-green-100' : 'bg-gray-200'
                      }`}
                    >
                      <Globe
                        size={20}
                        className={
                          localPermissionType === 'anyoneWithLink'
                            ? 'text-green-600'
                            : 'text-gray-600'
                        }
                      />
                    </div>
                    <div className='flex-1'>
                      <div className='flex items-center gap-2'>
                        <p className='text-sm font-medium text-gray-900'>
                          {t('knowledge.share.anyoneWithLink')}
                        </p>
                        {localPermissionType === 'anyoneWithLink' && (
                          <Check size={16} className='text-blue-600' />
                        )}
                      </div>
                      <p className='text-xs text-gray-500'>
                        {t('knowledge.share.anyoneWithLinkDesc')}
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Share with People Section - Only show when restricted */}
              {localPermissionType === 'restricted' && (
                <div>
                  <h3 className='text-sm font-medium text-gray-900 mb-3'>
                    {t('knowledge.share.grantAccess')}
                  </h3>

                  {/* Search Input */}
                  <div className='relative mb-4'>
                    <Search
                      className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'
                      size={18}
                    />
                    <input
                      ref={searchInputRef}
                      type='text'
                      value={query}
                      onChange={e => handleSearchChange(e.target.value)}
                      onFocus={() => query && setShowSearchDropdown(true)}
                      placeholder={t('knowledge.share.searchPlaceholder')}
                      className='w-full pl-10 pr-4 py-2.5 border border-blue-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm'
                      disabled={sharesLoading}
                      data-testid='user-search-input'
                    />

                    {/* Search Dropdown */}
                    {showSearchDropdown && query && availableSearchResults.length > 0 && (
                      <div
                        ref={searchDropdownRef}
                        className='absolute z-50 w-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-[240px] overflow-y-auto'
                        data-testid='user-search-dropdown'
                      >
                        {availableSearchResults.map(user => (
                          <button
                            key={user.id}
                            onClick={() => handleAddMember(user.id, user.full_name)}
                            disabled={pendingShareId === user.id}
                            className='w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left'
                            data-testid='user-search-result'
                          >
                            <div className='w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0'>
                              <User size={16} />
                            </div>
                            <div className='flex-1'>
                              <span className='text-sm font-medium text-gray-900 block'>
                                {user.full_name}
                              </span>
                              <span className='text-xs text-gray-500 block'>{user.email}</span>
                            </div>
                            {pendingShareId === user.id && (
                              <Loader2 size={16} className='text-blue-600 animate-spin' />
                            )}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Loading State */}
                    {showSearchDropdown && searchLoading && (
                      <div
                        ref={searchDropdownRef}
                        className='absolute z-50 w-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-4 px-4'
                      >
                        <div className='flex items-center justify-center space-x-2'>
                          <Loader2 size={16} className='text-blue-600 animate-spin' />
                          <p className='text-gray-500 text-sm'>{t('knowledge.share.searching')}</p>
                        </div>
                      </div>
                    )}

                    {/* No Results Message */}
                    {showSearchDropdown &&
                      query &&
                      !searchLoading &&
                      availableSearchResults.length === 0 && (
                        <div
                          ref={searchDropdownRef}
                          className='absolute z-50 w-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-4 px-4'
                          data-testid='no-users-found'
                        >
                          <p className='text-gray-500 text-sm text-center'>
                            {t('knowledge.share.noResults')}
                          </p>
                        </div>
                      )}
                  </div>

                  {/* Members List */}
                  {shares.length > 0 ? (
                    <div className='space-y-1' data-testid='shares-list'>
                      {sharesLoading ? (
                        <div className='flex items-center justify-center py-8'>
                          <Loader2
                            size={24}
                            className='text-blue-600 animate-spin'
                            data-testid='shares-loading-spinner'
                          />
                        </div>
                      ) : (
                        shares.map(share => (
                          <div
                            key={share.id}
                            className='flex items-center justify-between py-3 px-2 hover:bg-gray-50 rounded-lg transition-colors'
                          >
                            <div className='flex items-center gap-3'>
                              <div className='w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white flex-shrink-0'>
                                <span className='text-sm font-medium'>
                                  {share.shared_with_user.full_name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className='text-sm font-medium text-gray-900'>
                                  {share.shared_with_user.full_name}
                                </p>
                                <p className='text-xs text-gray-500'>
                                  {share.shared_with_user.email}
                                </p>
                              </div>
                            </div>

                            {/* Permission Dropdown Button */}
                            <button
                              onClick={e => handlePermissionMenuClick(e, share.id)}
                              disabled={pendingShareId === share.id}
                              className='flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors disabled:opacity-50'
                              data-testid='share-permission-button'
                            >
                              <span>
                                {share.permission === 'edit'
                                  ? t('knowledge.share.canEdit')
                                  : t('knowledge.share.canView')}
                              </span>
                              {pendingShareId === share.id ? (
                                <Loader2 size={14} className='animate-spin' />
                              ) : (
                                <ChevronDown size={14} />
                              )}
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    <div className='text-center py-6'>
                      <p className='text-gray-500 text-sm'>{t('knowledge.share.noMembers')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer Action Bar */}
          <div className='flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50'>
            <button
              onClick={handleCopyLink}
              disabled={isSaving}
              className='flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {linkCopied ? <Check size={16} className='text-green-600' /> : <Copy size={16} />}
              {t('knowledge.share.copy')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !hasUnsavedChanges}
              className='flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400'
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className='animate-spin' />
                  {t('common.save')}
                </>
              ) : (
                t('knowledge.share.save')
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Render dropdowns via portal to avoid positioning issues */}
      {openPermissionMenuId &&
        menuPosition &&
        createPortal(
          <>
            <div
              className='fixed inset-0 z-[60]'
              onClick={() => {
                setOpenPermissionMenuId(null);
                setMenuPosition(null);
              }}
            />
            <div
              className='fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[70] w-36'
              style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
              }}
            >
              <button
                onClick={() => handlePermissionChange(openPermissionMenuId, 'read')}
                className='w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between'
                data-testid='permission-read-option'
              >
                <span>{t('knowledge.share.canView')}</span>
                {getCurrentPermission(openPermissionMenuId) === 'read' && (
                  <Check size={14} className='text-blue-600' />
                )}
              </button>
              <button
                onClick={() => handlePermissionChange(openPermissionMenuId, 'edit')}
                className='w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between'
                data-testid='permission-edit-option'
              >
                <span>{t('knowledge.share.canEdit')}</span>
                {getCurrentPermission(openPermissionMenuId) === 'edit' && (
                  <Check size={14} className='text-blue-600' />
                )}
              </button>
              <div className='border-t border-gray-200 my-1' />
              <button
                onClick={() => handlePermissionChange(openPermissionMenuId, 'remove')}
                className='w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2'
                data-testid='permission-remove-option'
              >
                <X size={14} />
                <span>{t('knowledge.share.remove')}</span>
              </button>
            </div>
          </>,
          document.body
        )}
    </>
  );
};
